// Read-only cross-month audit report. Existing upload and deletion workflows remain unchanged.
import React, { useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { loadRideHailingWeekPeriods, getConfiguredWeekRange, RIDE_HAILING_WEEK_KEYS } from "./rideHailingWeekPeriodUtils";

// Mirror the existing RideHailingUploadHistory options so filters work before any report is generated.
const COMPANY_OPTIONS = [
  "Lolia Limousine for Luxury Motor Vehicles Services LLC",
  "Comfort Line Luxury Motor Vehicles Services LLC",
];
const PLATFORM_OPTIONS = ["bolt", "uber", "careem", "yango", "other"];
const normalize = value => String(value || "").trim().toLowerCase();
const pad = n => String(n).padStart(2, "0");
const isoDate = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const now = new Date();
const initialEnd = isoDate(now);
const initialStart = `${now.getFullYear()}-01-01`;
const input = { padding: 10, border: "1px solid #cbd5e1", borderRadius: 7 };
const th = { padding: "11px 10px", textAlign: "left", borderBottom: "1px solid #cbd5e1", background: "#f1f5f9" };
const td = { padding: "10px", borderBottom: "1px solid #e2e8f0" };
const cash = n => Number(n || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const keyFor = (id, month, week, company, platform) => [id, month, week, String(company||"").trim().toLowerCase(), String(platform||"").trim().toLowerCase()].join("\u0001");
const monthKeys = (start, end) => {
  const result = [];
  const [sy, sm] = start.slice(0,7).split("-").map(Number);
  const [ey, em] = end.slice(0,7).split("-").map(Number);
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    result.push(`${y}-${pad(m)}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return result;
};
function downloadCSV(rows) {
  const headers = ["Company", "Platform", "Month", "Week", "Period Start", "Period End", "Uploaded At", "Batch ID", "Source File", "History Rows", "Matched Rows", "Bank AED", "Cash AED", "Total AED", "Status"];
  const line = cells => cells.map(x=>`"${String(x ?? "").replace(/"/g,'""')}"`).join(",");
  const csv = [headers, ...rows.map(r=>[r.company,r.platform,r.month,r.week,r.startDate,r.endDate,r.createdAt,r.uploadBatchId,r.sourceFileName,r.totalRows,r.matchedRows,r.bank,r.cash,r.bank+r.cash,r.status])].map(line).join("\r\n");
  const blob = new Blob(["\ufeff",csv], {type:"text/csv;charset=utf-8"});
  const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download="ride_hailing_batch_range_report.csv"; a.click(); URL.revokeObjectURL(url);
}
export default function RideHailingRangeReport() {
  const [startDate,setStartDate]=useState(initialStart);
  const [endDate,setEndDate]=useState(initialEnd);
  const [company,setCompany]=useState("all");
  const [platform,setPlatform]=useState("all");
  const [records,setRecords]=useState([]);
  const [status,setStatus]=useState("");
  const [working,setWorking]=useState(false);
  const [loaded,setLoaded]=useState(false);
  async function run() {
    setLoaded(false);setRecords([]);
    if (!startDate || !endDate || startDate>endDate) return setStatus("Enter a valid start and end date.");
    const months=monthKeys(startDate,endDate);
    if(months.length>12) return setStatus("Select up to 12 calendar months per report to limit Firestore reads.");
    setWorking(true);
    try {
      setStatus("Reading weekly batch history and configured week periods...");
      const history=[];
      for(const month of months){
        const config=await loadRideHailingWeekPeriods(month);
        for(const week of RIDE_HAILING_WEEK_KEYS){
          const period=getConfiguredWeekRange(config,week,month);
          if(!period || period.endDate<startDate || period.startDate>endDate) continue;
          const snap=await getDocs(collection(db,"weeklyEarningsUploads",month,week));
          snap.forEach(ds=>{
            const d=ds.data();
            const dt=d.createdAt?.toDate?.();
            history.push({ id:ds.id, uploadBatchId:String(d.uploadBatchId??"").trim(), company:d.company||"(Unspecified)", platform:String(d.platform||"other").toLowerCase(), month, week, startDate:period.startDate, endDate:period.endDate, createdAt:dt?dt.toLocaleString("en-GB"):"", timestamp:dt?.getTime()||0, sourceFileName:d.sourceFileName||"", totalRows:Number(d.totalRows||0), matchedRows:0, bank:0,cash:0 });
          });
        }
      }
      // Avoid reading every driver when there are no matching batches.
      if(!history.length){setRecords([]);setLoaded(true);setStatus("No upload history found in the selected reporting periods.");return;}
      const wanted=new Map();
      history.forEach(h=>{
        const key=keyFor(h.uploadBatchId,h.month,h.week,h.company,h.platform);
        if(!wanted.has(key)) wanted.set(key,[]);
        wanted.get(key).push(h);
      });
      setStatus(`Found ${history.length} upload records. Reconciling bank/cash against driver rows...`);
      const driverSnap=await getDocs(collection(db,"driverWeeklyEarnings"));
      let scanned=0;
      // Deliberately read-only; same underlying driver uploads collection used by delete preview.
      // Sequential reads reduce burst load; this may take longer for a large driver fleet.
      for(const driver of driverSnap.docs){
        const rows=await getDocs(collection(db,"driverWeeklyEarnings",driver.id,"uploads"));
        rows.forEach(ds=>{
          const d=ds.data();
          const k=keyFor(String(d.uploadBatchId??"").trim(),d.month,d.week,d.company,d.platform);
          const matched=wanted.get(k);
          if(matched) matched.forEach(h=>{h.matchedRows++;h.bank+=Number(d.bankIncome||0);h.cash+=Number(d.cashIncome||0);});
        });
        scanned++;
        if(scanned%50===0) setStatus(`Reconciled ${scanned} of ${driverSnap.size} driver records...`);
      }
      const result=history.map(h=>({...h,status:h.matchedRows===h.totalRows?"Matched":"Review row count"})).sort((a,b)=>a.company.localeCompare(b.company)||a.platform.localeCompare(b.platform)||a.month.localeCompare(b.month)||a.week.localeCompare(b.week)||b.timestamp-a.timestamp);
      setRecords(result);setLoaded(true);
      setStatus(`Report completed: ${result.length} batches; ${driverSnap.size} driver records checked. Dates refer to configured earning weeks, not upload timestamps.`);
    }catch(e){console.error(e);setStatus(`Report failed: ${e.message||e}. No data was changed.`);}finally{setWorking(false);}
  }
  const filtered=records.filter(r=>(company==="all"||normalize(r.company)===normalize(company))&&(platform==="all"||normalize(r.platform)===normalize(platform)));
  const groups=Array.from(filtered.reduce((m,r)=>{
    const k=r.company+"\u0001"+r.platform;
    const obj=m.get(k)||{company:r.company,platform:r.platform,batches:0,rows:0,bank:0,cash:0};
    obj.batches++;obj.rows+=r.matchedRows;obj.bank+=r.bank;obj.cash+=r.cash;m.set(k,obj);return m;
  },new Map()).values());
  const totals=groups.reduce((a,g)=>({batches:a.batches+g.batches,rows:a.rows+g.rows,bank:a.bank+g.bank,cash:a.cash+g.cash}),{batches:0,rows:0,bank:0,cash:0});
  const companies=[...new Set([...COMPANY_OPTIONS,...records.map(r=>r.company).filter(Boolean)])].sort();
  const platforms=[...new Set([...PLATFORM_OPTIONS,...records.map(r=>r.platform).filter(Boolean)])].sort();
  return <div style={{padding:24,maxWidth:1450,margin:"auto",color:"#1f2937"}}>
    <h2>Ride-Hailing Upload Report — Date Range</h2>
    <p>Company-wise and platform-wise uploaded batch count, bank and cash. Read-only: no changes to uploading or deleting.</p>
    <div style={{display:"flex",flexWrap:"wrap",gap:16,alignItems:"end",padding:18,border:"1px solid #dbe3ee",borderRadius:10,background:"white"}}>
      <label>From date<br/><input type="date" style={input} value={startDate} onChange={e=>setStartDate(e.target.value)}/></label>
      <label>To date<br/><input type="date" style={input} value={endDate} onChange={e=>setEndDate(e.target.value)}/></label>
      <button disabled={working} onClick={run} style={{...input,color:"white",background:"#2563eb",cursor:"pointer"}}>{working?"Loading...":"Generate Report"}</button>
      <label>Company<br/><select style={input} value={company} onChange={e=>setCompany(e.target.value)}><option value="all">All companies</option>{companies.map(c=><option key={c}>{c}</option>)}</select></label>
      <label>Platform<br/><select style={input} value={platform} onChange={e=>setPlatform(e.target.value)}><option value="all">All platforms</option>{platforms.map(p=><option key={p}>{p}</option>)}</select></label>
      <button disabled={!loaded || !filtered.length} onClick={()=>downloadCSV(filtered)} style={input}>Download CSV</button>
    </div>
    {status&&<p role="status" style={{color:status.startsWith("Report failed")?"#b91c1c":"#475569"}}>{status}</p>}
    {loaded&&<>
      <h3>Summary — Company and Platform</h3>
      <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",background:"white"}}><thead><tr>{["Company","Platform","Batches","Driver rows","Bank (AED)","Cash (AED)","Total (AED)"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead><tbody>
        {groups.map(g=><tr key={g.company+g.platform}>{[g.company,g.platform,g.batches,g.rows,cash(g.bank),cash(g.cash),cash(g.bank+g.cash)].map((v,i)=><td key={i} style={td}>{v}</td>)}</tr>)}
        <tr style={{fontWeight:800}}>{["TOTAL","",totals.batches,totals.rows,cash(totals.bank),cash(totals.cash),cash(totals.bank+totals.cash)].map((v,i)=><td key={i} style={td}>{v}</td>)}</tr>
      </tbody></table></div>
      <h3>Each Uploaded Batch</h3><div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",background:"white",fontSize:13}}><thead><tr>{["Company","Platform","Month / Week","Earning period","Batch ID","Uploaded","Source file","History rows","Matched rows","Bank AED","Cash AED","Total AED","Status"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead><tbody>
      {filtered.map(r=><tr key={r.id+r.month+r.week}>{[r.company,r.platform,`${r.month} / ${r.week}`,`${r.startDate} to ${r.endDate}`,r.uploadBatchId,r.createdAt,r.sourceFileName,r.totalRows,r.matchedRows,cash(r.bank),cash(r.cash),cash(r.bank+r.cash),r.status].map((v,i)=><td key={i} style={{...td,color:i===12&&r.status!=="Matched"?"#b45309":undefined}}>{v}</td>)}</tr>)}
      </tbody></table></div>
      <p style={{color:"#64748b",fontSize:13}}>A selected date range includes configured weekly earning periods that overlap the range. For partial weeks, the entire batch is included; totals cannot be split by individual earning dates from these stored weekly records. Repeated uploads are counted separately. Missing driver rows are flagged for review.</p>
    </>}
  </div>;
}
